/**
 * Flight/date display utilities + Places session token, extracted from
 * EventListModule (PR3). Byte-identical moves — no behavior change.
 * generateSessionToken is moved verbatim (manual v4 polyfill used as the Google
 * Places autocomplete session token for billing grouping; do NOT swap to
 * crypto.randomUUID() in this structural PR).
 */

export function generateSessionToken(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Calculate travel duration between two times
 * @param depTime Departure time in HH:MM format (24-hour)
 * @param arrTime Arrival time in HH:MM format (24-hour)
 * @param depDate Departure date in YYYY-MM-DD format
 * @param arrDate Arrival date in YYYY-MM-DD format
 * @returns Formatted duration string like "1 hr 35 min"
 */
export function calculateTravelDuration(depTime: string, arrTime: string, depDate?: string, arrDate?: string): string {
  if (!depTime || !arrTime) return '';

  try {
    // Use dates if provided, otherwise use same day
    const baseDate = depDate || '2000-01-01';
    const endDate = arrDate || depDate || '2000-01-01';

    const depDateTime = new Date(`${baseDate}T${depTime}:00`);
    const arrDateTime = new Date(`${endDate}T${arrTime}:00`);

    if (isNaN(depDateTime.getTime()) || isNaN(arrDateTime.getTime())) {
      return '';
    }

    const durationMs = arrDateTime.getTime() - depDateTime.getTime();
    const durationMinutes = Math.floor(durationMs / (1000 * 60));

    if (durationMinutes < 0) return '';

    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;

    if (hours === 0) {
      return `${minutes} min`;
    } else if (minutes === 0) {
      return `${hours} hr`;
    } else {
      return `${hours} hr ${minutes} min`;
    }
  } catch {
    return '';
  }
}

/**
 * Format time from HH:MM (24-hour) to 12-hour format with AM/PM
 */
export function formatTime12Hour(time: string): string {
  if (!time) return '';
  const [hourStr, minuteStr] = time.split(':');
  const hour = parseInt(hourStr, 10);
  const minute = minuteStr;

  if (isNaN(hour)) return '';

  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const ampm = hour < 12 ? 'AM' : 'PM';
  return `${hour12}:${minute} ${ampm}`;
}

/**
 * Calculate layover time between two flight segments
 * @param arrivalTime Arrival time of first segment (ISO string)
 * @param departureTime Departure time of next segment (ISO string)
 * @returns Formatted layover duration string like "2 hr 30 min"
 */
export function calculateLayoverTime(arrivalTime: string, departureTime: string): string {
  try {
    const arrival = new Date(arrivalTime);
    const departure = new Date(departureTime);

    if (isNaN(arrival.getTime()) || isNaN(departure.getTime())) {
      return '';
    }

    const layoverMs = departure.getTime() - arrival.getTime();
    if (layoverMs < 0) return '';

    const layoverMinutes = Math.floor(layoverMs / (1000 * 60));
    const hours = Math.floor(layoverMinutes / 60);
    const minutes = layoverMinutes % 60;

    if (hours === 0) {
      return `${minutes} min layover`;
    } else if (minutes === 0) {
      return `${hours} hr layover`;
    } else {
      return `${hours} hr ${minutes} min layover`;
    }
  } catch {
    return '';
  }
}

/**
 * Format date from ISO string to short format
 * @param isoString ISO date string
 * @returns Formatted date like "01/12/26"
 */
export function formatFlightDate(isoString: string): string {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    return `${month}/${day}/${year}`;
  } catch {
    return '';
  }
}

export function formatLastSyncedAt(value: string | null): string {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}
